import type { Editor } from '@tiptap/core'
import type { ForwardedRef } from 'react'
import React, {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import Button from '@/components/shared/ui/Button'
import Dialog from '@/components/shared/ui/Dialog'
import {
  DialogClose,
  DialogContent,
  DialogTitle,
} from '@/components/shared/ui/Dialog/Dialog'
import Input from '@/components/shared/ui/Input'
import { useTranslations } from '@/hooks/useTranslations'
import { RegexValidator } from '@/lib/helpers/stringHelpers'

interface IPropTypes {
  editor: Editor
}
const EditorLinkDialog = forwardRef(
  (props: IPropTypes, ref: ForwardedRef<any>) => {
    const { editor } = props
    const [open, setOpen] = React.useState(false)
    const [linkURL, setLinkURL] = useState<string | null>(null)
    const t = useTranslations('editor')

    const handleLink = (link: string | null) => {
      if (link && link.trim().length) {
        editor
          .chain()
          .focus()
          .extendMarkRange('link')
          .setLink({ href: link })
          .run()
      } else editor.chain().focus().unsetLink().run()
      return setOpen(false)
    }

    useImperativeHandle(ref, () => ({
      openDialog: () => {
        const previousUrl = editor.getAttributes('link').href
        setLinkURL(previousUrl || '')
        setOpen(true)
      },
    }))

    const isValidURL = useMemo(
      () => RegexValidator.isValid(linkURL || '', 'reg_url'),
      [linkURL]
    )

    return (
      <Dialog
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
      >
        <DialogContent>
          <DialogClose />
          <DialogTitle>{t('add_link_to')}</DialogTitle>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              e.stopPropagation()
              return handleLink(linkURL)
            }}
            className='mt-2'
          >
            <Input
              autoFocus
              placeholder={t('placeholder')}
              value={linkURL || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setLinkURL(e.target.value.trim())
              }
              type='url'
              required
            />
            <button type='submit' className='sr-only'>
              {t('buttons.submit', { ignorePrefix: true })}
            </button>
          </form>
          <div className='mt-4 flex justify-end space-x-1'>
            <Button
              variant='danger'
              onClick={() => handleLink(null)}
              disabled={!linkURL}
            >
              {t('button.unlink')}
            </Button>
            <Button onClick={() => handleLink(linkURL)} disabled={!isValidURL}>
              {t('link')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    )
  }
)

EditorLinkDialog.displayName = 'EditorLinkDialog'
export default EditorLinkDialog
